<template>
    <div>
        <MatcapProvider>
            <MonitorGLWidget v-if="params.stats" />

            <BackgroundEffects>
                <SceneCamera ref="refCamera" v-bind="{ introProgress: 0, openProgress: 0 }">
                    <Box ref="refBox" :image="box?.image" :color="box?.color">
                        <component
                            ref="refPack"
                            v-if="packProps"
                            v-bind="packProps"
                            :is="PackComponent"
                        >
                            <Cards
                                ref="refCards"
                                v-if="cards.length"
                                :cards="cards"
                                :cardIndex="cardIndex"
                            />

                            <GoldenTicket
                                ref="refTicket"
                                :revealed="goldenTicket?.revealed"
                                :data="goldenTicket || {}"
                                v-if="goldenTicket"
                            />
                        </component>
                    </Box>
                </SceneCamera>
                
                <SimpleLines
                ref="refUserInfo"
                :text="box?.pack?.username"
                color="#4327DF"
                :opacity="0.1"
                />
            </BackgroundEffects>

            <GLTextInit />
        </MatcapProvider>
    </div>
</template>

<script setup>
    import { useEnvMapLoader, useEnvVar } from '#imports';
    import {
        Complete,
        PACK_TYPE_REPACK,
        PACK_TYPE_WAX,
        StashOrPass,
        StepBoxReveal,
        StepCardsReveal,
        StepGoldenTicketResult,
        StepGoldenTicketReveal,
        StepIdle,
        StepPackReveal,
    } from '@fanatics/resn-api/instant-rips';
    import { computed, onMounted, onUnmounted, reactive, ref, shallowRef, watch } from 'vue';

    import { randomInt } from '@resn/gozer-math';
    import { urlSearchParams } from '@resn/gozer-misc';
    import { usePane, useRenderer, useResize } from '@resn/gozer-vue';
    import { gsap } from '@resn/gsap';

    import Box from '../Box.vue';
    import Cards from '../Cards/Cards.vue';
    import SimpleLines from '../common/SimpleLines.vue';
    import GLTextInit from '../GLTextInit.vue';
    import Pack, { TRANSITIONS_RIP } from '../Pack/Pack.vue';
    import PackWax, { TRANSITIONS_RIP as TRANSITIONS_RIP_WAX } from '../Pack/PackWax.vue';
    import SceneCamera from '../SceneCamera';
    import GoldenTicket from './objects/GoldenTicket/GoldenTicket.vue';
    import MonitorGLWidget from '~/components/gl/MonitorGLWidget.vue';
    import { useSandBox } from '~/composables/useSandbox';
    import useThreeMainScene from '~/composables/useThreeMainScene';
    import { useThreeOrthoObject } from '~/composables/useThreeOrthoObject';
    import { CARDS_MODE } from '~/core/constants';
    import { useAudio } from '~/providers/AudioProvider';
    import { useColorPalette } from '~/providers/ColorPaletteProvider';
    import { useFanatics } from '~/providers/instant-rips/FanaticsProvider';
    import { MatcapProvider } from '~/providers/MatcapProvider';
    import BackgroundEffects from '~/components/gl/BackgroundEffects/BackgroundEffects.vue';

    const { renderer, orthoCamera, scene } = useRenderer();

    const fanatics = useFanatics();
    const { step, cardIndex, pack, cards, box, goldenTicket, sceneOffset, events } = fanatics;
    // Check if current pack is a wax pack
    const isSandbox = useSandBox();
    const isPackWax = computed(() => pack.value?.type === PACK_TYPE_WAX);

    const { colorPalette, setColorPalette } = useColorPalette();
    const audio = useAudio();

    const refBox = ref(null);
    const refPack = ref(null);
    const refCards = ref(null);
    const refTicket = ref(null);
    const refCamera = ref(null);

    const boxLoaded = ref(false);
    const packLoaded = ref(false);
    const loaded = computed(() => boxLoaded.value && packLoaded.value);

    // const types = {
    //     [PACK_TYPE_WAX]: PackWax,
    //     [PACK_TYPE_REPACK]: Pack,
    // };
    const PackComponent = computed(() => (isPackWax.value ? PackWax : Pack));

    // Unwrap pack for safe v-bind usage in template
    // This prevents destructuring errors when joining midway with existing pack data
    const packProps = computed(() => pack.value || null);

    const refUserInfo = ref(null);
    const { loadEnvMap, dispose: disposeEnvMaps } = useEnvMapLoader();

    const params = reactive({
        stats: !isSandbox
            ? useEnvVar('ENABLE_MONITOR_GL')
            : Boolean(urlSearchParams.get?.('stats')),
    });

    const hasGoldenTicketAudio = computed(
        () => goldenTicket.value && step.value == StepGoldenTicketReveal
    );

    const sceneCamera = shallowRef(null);

    // provide('scene', { camera: sceneCamera });

    useThreeOrthoObject(null, { name: 'OrthographicRoot', root: true });

    onMounted(() => {
        events.on(Complete, onComplete);
        // addSceneToPrerender({ id: 'mainScene', scene });

        // Load and generate environment map from EXR texture
        // loadEnvMap(renderer, '/textures/env-map/env-map-global.exr');

        sceneCamera.value = refCamera.value.camera;

        setCamera(sceneCamera.value);
        setupComposer();
        setCameraOffset();

        window.renderer = renderer;
        window.box = refBox.value;
        window.pack = refPack.value;
    });

    const onLoaded = () => {
        // reset();
        onStep(step.value);
    };

    onUnmounted(() => {
        events.off(Complete, onComplete);
        disposeEnvMaps();
    });

    const { setCamera, refreshSceneHelpers, composer } = useThreeMainScene({
        id: 'MainScene',
        enableStats: params.stats,
    });

    const setupComposer = () => {
        const { camera } = refCamera.value;
        composer.setup({ renderer, scene, camera, orthoCamera });
    };

    const setCameraOffset = () => {
        if (!sceneCamera.value) return;

        const { width, height } = viewport;

        const x = 0;
        const y = sceneOffset.value;
        sceneCamera.value.setViewOffset(width, height, x, y, width, height);
        sceneCamera.value.updateProjectionMatrix();
    };

    const viewport = useResize(() => {
        setCameraOffset();
    }, true);

    const onStep = (step, stepPrev) => {
        let transitionRipId;

        switch (step) {
            case StepIdle:
                reset();
                break;
            case StepBoxReveal:
                refCamera.value?.show();
                refBox.value?.show();

                gsap.delayedCall(0.1, () => audio.play('boxOpen'));
                break;
            case StepPackReveal:
                if (stepPrev == StepBoxReveal) refBox.value?.open();

                refCamera.value?.open();

                // Ensure pack is reset between consecutive packs
                if (stepPrev !== StepBoxReveal) refPack.value?.reset();
                refPack.value?.show({
                    transitionId: stepPrev == StepBoxReveal ? null : 'flyIn',
                    spinDir: stepPrev == StepBoxReveal ? 1 : 1,
                    spinDelay: stepPrev == StepBoxReveal ? 0.8 : 1.8,
                });

                refUserInfo.value?.show({ delay: stepPrev == StepBoxReveal ? 0 : 1 });

                audio.play('boxReveal');
                break;
            case StepCardsReveal:
            case StepGoldenTicketReveal:
            case StepGoldenTicketResult:
                var shouldOpenPack =
                    stepPrev == StepPackReveal || stepPrev == StepBoxReveal || stepPrev == StepIdle;

                if (shouldOpenPack) {
                    const transitionsRip = isPackWax.value ? TRANSITIONS_RIP_WAX : TRANSITIONS_RIP;
                    transitionRipId =
                        Object.keys(transitionsRip)[
                            randomInt(0, Object.keys(transitionsRip).length - 1)
                        ];
                    refPack.value?.open({ transitionId: transitionRipId });
                }

                if (step == StepGoldenTicketReveal) {
                    refTicket.value?.show({ delay: 0.2 });
                } else if (step == StepCardsReveal) {
                    const isRip = transitionRipId == 'rip' || transitionRipId == 'ripAndBurn';
                    refCards.value?.show({
                        delay: isPackWax.value ? 0.35 : 0,
                        delayPosition: isPackWax.value ? 0.5 : isRip ? 1 : 0.8,
                    });
                } else if (step == StepGoldenTicketResult) {
                    if (goldenTicket.value.ticketType == StashOrPass)
                        gsap.timeline()
                            .add(refCards.value?.show())
                            .add(() => refCards.value?.switchMode(CARDS_MODE.FAN));
                }

                break;

            default:
                break;
        }

        refCamera.value?.setStep(step);

        const shouldHideBox = step !== StepBoxReveal && step !== StepIdle;
        const delayHideBox = step == StepPackReveal && stepPrev === StepBoxReveal ? 2 : 0;
        const delayHidePack =
            step == StepGoldenTicketReveal ||
            step == StepCardsReveal ||
            step == StepGoldenTicketResult
                ? isPackWax.value
                    ? 8
                    : // ? 2.5
                      2
                : 0;
        const instantHideCard = stepPrev !== StepPackReveal;

        if (shouldHideBox) refBox.value?.hide({ delay: delayHideBox });
        if (step !== StepPackReveal) refPack.value?.hide({ delay: delayHidePack });
        if (step !== StepGoldenTicketReveal) refTicket.value?.hide();
        if (step !== StepCardsReveal && step !== StepGoldenTicketResult)
            refCards.value?.hide({ force: instantHideCard });

        if (step !== StepCardsReveal) {
            setColorPalette(null);
        }

        refreshSceneHelpers();
    };

    const onCardIndex = () => {
        if (step.value === StepCardsReveal) audio.play('nextPrev');
    };

    const onHasGoldenTicketAudio = (value) => {
        if (value) audio.play('goldenTicketAmbient', { fade: true, ignoreUnlocked: true });
        else audio.pause('goldenTicketAmbient', { fade: true });
    };

    const onComplete = () => {
        refCards.value?.hide();
        audio.play('fadeAway');
    };

    /*     const setLightsDown = (val) => {
        if (audio.disableAmbientAudio.value) return;
        if (val) audio.pause('cardStaticState', { fade: true });
        else audio.play('cardStaticState', { fade: true });
    };
    watch(() => fanatics.fgDimmed.value, setLightsDown); */

    // const setGoldenTicket = () => setColorPalette(null);
    // watch(goldenTicket, setGoldenTicket);

    watch(loaded, onLoaded);
    watch(step, onStep);
    watch(cardIndex, onCardIndex);
    watch(hasGoldenTicketAudio, onHasGoldenTicketAudio);

    const reset = () => {
        refCamera.value?.reset();
        refBox.value?.reset();
        refPack.value?.reset();
        refCards.value?.reset();
        refTicket.value?.reset();
    };

    defineExpose({ reset });
</script>
